import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import GridViewModel from '@/ViewModels/Controls/GridViewModel';
import Label from '@/Controls/Label';
import MessageBoxViewModel from '@/ViewModels/Controls/MessageBoxViewModel';
import RADApplication from '@/Management/RADApplication';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import {DisposeBehavior, ICoreFrameGridBindingParameter, IVerticalResizeOptions} from '#/DefaultViewExtensionInterfaces';
import * as NumericTypeHelper from '@/Utility/NumericTypeHelper';
import ResourceManager from '@/Management/ResourceManager';
import GridVerticalResizeManager from '@/Utility/GridVerticalResizeManager';
import { ICoreFrameGrid } from "rad-coreframe-grid";

export default class CoreFrameGridControl extends Control {

    protected control: JQuery;
    private initiallyRendered;
    public label: any;
    private suppressWidgetCreatedCallback: boolean;
    protected isNoEntriesElementVisible: KnockoutObservable<boolean>;
    private verticalResizeManager?: GridVerticalResizeManager;

    public constructor(protected viewModel: GridViewModel, private element?: JQuery) {
        super(viewModel, element);

        this.initiallyRendered = false;

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));

        this.isNoEntriesElementVisible = this.createNoEntriesElementVisibleObs();
        this.verticalResizeManager = this.createVerticalResizeManager();
    }

    protected createNoEntriesElementVisibleObs(): KnockoutObservable<boolean> {
        return this.defineComputed(() => {
            if (!this.viewModel.showNoEntriesLabel)
                return false;

            const isBusy = this.viewModel.isBusy();
            const hasNoData = this.viewModel.data().length === 0;
            return !isBusy && hasNoData;
        });
    }

    private regenerateColumGroupsIfNeeded(): void {
        const grid = this.getControl().data('coreframegrid') as ICoreFrameGrid;
        grid?.regenerateColumGroupsIfNeeded();
    }

    public getControl(): JQuery {
        if (this.control != null) {
            return this.control;
        }

        this.control = this.element || $('<div></div>');

        if (this.viewModel.noRightsLabel != null) {
            const label = new Label(this.viewModel.noRightsLabel);
            const tuple = label.getTuple();

            tuple.control.appendTo(this.control);
        }

        this.applyBindingsForControl();

        if (this.viewModel.shouldLoadData())
            this.applyGridDataBindings();
        else // applyGridDataBindings stößt den Callback an, daher müssen wir ihn hier explizit aufrufen
            this.widget(null);
        
        return this.control;
    }

    private _waitingForRender = false;
    public onBeforeRenderCallback() {
        if (this.isDisposed)
            return;

        // sollte durch das Grid gesetzt werden (der Callback wird ausgeführt, wenn das Grid anfängt zu arbeiten) um etwaige Loader zum
        // richtigen Zeitpunkt zu setzen
        this.viewModel.increaseBusyCounter();
        this._waitingForRender = true;
    }

    public onAfterRenderCallback() {
        if (this.isDisposed)
            return;

        this._waitingForRender = false;
        this.regenerateColumGroupsIfNeeded();

        // sollte durch das Grid gesetzt werden, da das Grid genau weiß, wann es fertig ist (der Callback wird ausgeführt, wenn alles dargestellt ist)
        // Loader wird entfernt
        this.viewModel.decreaseBusyCounter();

        if (!this.initiallyRendered) {
            this.addEventHandler('click',
                this.control,
                () => {
                    if (this.viewModel.isLimited()) {
                        RADApplication.getInstance().showMessageBox(MessageBoxViewModel.createNoticeMessageBox(null, 'DATAGRID_LIMIT_NOSORTING'));
                    }
                },
                'th');

            this.initiallyRendered = true;
        }
    }

    protected onWidgetCreatedCallback(widget: null) {
        if (this.suppressWidgetCreatedCallback)
            return;

        if (!this.viewModel.shouldLoadData()) {
            let subscription = this.viewModel.shouldLoadData.subscribe(
                (shouldLoadData) => {
                    if (!shouldLoadData)
                        return;

                    this.suppressWidgetCreatedCallback = true;
                    this.applyGridDataBindings();
                    this.suppressWidgetCreatedCallback = false;

                    subscription.dispose();
                });
        }
    }

    protected applyBindingsForControl(): void {
        this.applyBindings(this.control, {
            controlName: 'coreframegrid',
            loader: this.defineComputed(() => {
                return this.viewModel.isBusy();
            }),
            controlIdent: this.identifier,
            visible: this.viewModel.isVisible,
            tooltip: this.viewModel.tooltip,
            invalid: this.viewModel.invalidationText,
            styles: this.definePureComputed({
                read: () => {
                    const styles = ['default'];

                    return styles;
                }
            }),
            states: this.definePureComputed({
                read: () => {
                    let states = [];

                    if (this.viewModel.isRequired()) {
                        states.push('required');
                    }

                    if (this.viewModel.isValid()) {
                        states.push('valid');
                    } else {
                        states.push('invalid');
                    }

                    if (this.viewModel.isEnabled()) {
                        states.push('enabled');
                    } else {
                        states.push('disabled');
                    }

                    return states;
                }
            })
        });
    }

    protected applyGridDataBindings(): void {
        const gridParams: Partial<ICoreFrameGridBindingParameter> = {
            columns: this.viewModel.columns,
            data: this.viewModel.data,
            sortable: this.definePureComputed({
                read: () => {
                    if (this.viewModel.isLimited() || !this.viewModel.isSortable())
                        return false;

                    return {
                        mode: this.viewModel.isMultiColumnSort() ? 'multiple' : 'single',
                        allowUnsort: this.viewModel.allowUnsort()
                    };
                }
            }),
            sortingClickStrategy: GlobalConfiguration.dataGrid.SortingClickStrategy,
            height: this.viewModel.height,
            editable: this.viewModel.isEditable,
            paging: this.definePureComputed({
                read: () => {
                    return {
                        isEnabled: this.viewModel.hasPager(),
                        custom: true,
                        currentPage: this.viewModel.page(),
                        pageSize: this.viewModel.pageSize(),
                        pageSizes: this.viewModel.pageSizes(),
                        totalRows: this.viewModel.totalRows(),
                        onPageSizeChanged: this.viewModel.onPageSizeChanged.bind(this.viewModel),
                        onPageChanged: this.viewModel.onPageChanged.bind(this.viewModel)
                    };
                }
            }),
            summaryRow: this.defineComputed(() => {
                return {
                    isEnabled: this.viewModel.hasSummaryRow(),
                    preText: `${this.viewModel.summaryRowPreText.caption()} `,
                    text: NumericTypeHelper.toLocaleString(this.viewModel.realTotalRows(), 0),
                    postText: (this.viewModel.isLimited() ? ` (${this.viewModel.summaryRowPostText.caption()})` : '')
                }
            }),
            totalRow: {
                isEnabled: this.viewModel.hasTotalRow(),
                refreshTrigger: (this.viewModel as any)?._refreshTotalRow // private member
            },
            selectionMode: this.definePureComputed({
                read: () => {
                    if (this.viewModel.isMultiColumnSelect()) {
                        return 'multiple';
                    } else {
                        return 'single';
                    }
                }
            }),
            selected: this.viewModel.selected,

            onSortChange: this.viewModel.onSortChanged.bind(this.viewModel),
            onDblClick: (id: any, rowData: any, evt: JQuery.Event) => {
                this.viewModel.onDoubleClick(id, rowData, evt.ctrlKey);
            },
            onMouseOver: this.viewModel.onMouseOver.bind(this.viewModel),
            onMouseOut: this.viewModel.onMouseOut.bind(this.viewModel),
            onSelectionChange: this.viewModel.onSelectionChanged.bind(this.viewModel),
            onColumnResized: this.viewModel.onColumnResized.bind(this.viewModel),
            onBeforeRender: this.onBeforeRenderCallback.bind(this),
            onAfterRender: this.onAfterRenderCallback.bind(this),
            onKeyDown: (id: any, rowData: any, evt: JQuery.Event) => {
                // Übergreifend für alle Grids: Wenn Leerzeile gedrückt, soll das Klick-Event ausgelöst werden

                if (evt.keyCode === 32) {
                    this.viewModel.onDoubleClick(id, rowData, evt.ctrlKey);
                    return;
                }
                this.viewModel.onKeyDown(id, rowData);
            },
            onHeightReset: () => {
                this.verticalResizeManager?.resetUserStorageAndTriggerRecalculation();
            },
            defaultHeight: this.viewModel.defaultHeight,
            widget: this.widget,
            showHeader: this.viewModel.showHeader,
            locator: this.viewModel.locator,
            rowIdName: this.viewModel.identity,
            orderBy: this.viewModel.orderBy,
            renderGridAsync: this.viewModel.renderGridAsync,
            tooltip: this.viewModel.tooltipOptions,
        };

        this.verticalResizeManager?.manage(gridParams);

        this.applyBindings(this.control, {
            loader: this.defineComputed(() => {
                return this.viewModel.isBusy();
            }),
            coreFrameGrid: gridParams
        });

        this.setupNoEntriesElement();
    }

    protected createVerticalResizeManager(): GridVerticalResizeManager | undefined {
        const verticalResize = this.viewModel.verticalResize;
        if (!verticalResize)
            return undefined;

        const verticalResizeManager = new GridVerticalResizeManager(verticalResize);

        // Wenn sich die Daten des Grids aktualisieren, soll die Höhenberechnung angestoßen werden
        verticalResizeManager.registerAutoHeightDependency(this.viewModel.data, () => false);

        // Solange das Grid noch am Rendern ist, muss keine Höhenberechnung angestoßen werden.
        // Dies soll erst geschehen, wenn das Grid fertig gerendert ist.
        verticalResizeManager.registerAutoHeightDependency(this.viewModel.isBusy, busy => busy);

        // Da sowohl isNoEntriesElementVisible als auch die automatische Höhenberechnung von der Anzahl der Zeilen
        // abhängig sind, kann es vorkommen, dass die Höhenberechnung zuerst (einen Tick vor isNoEntriesElementVisible)
        // ausgelöst wird. In diesem Fall würde das "no-entries"-Element in die Höhenberechnung einfließen und eine
        // falsche Höhe berechnet werden. Deshalb lassen wir die automatische Höhenberechnung erneut auslösen, wenn
        // sich isNoEntriesElementVisible ändert.
        verticalResizeManager.registerAutoHeightDependency(this.isNoEntriesElementVisible, () => false);

        // Wenn sich die Sichtbarkeit ändert (z.B. durch einen umgebenden Tab), kann sich auch die Darstellung ändern.
        // Wenn das Grid nicht dargestellt wird, kann die Berechnung abgebrochen werden.
        // betrifft hier auch den "isVisible"-State -> wenn bswp. ueber einen UIListener ein-/ausgeblendet wird
        verticalResizeManager.registerAutoHeightDependency(this.viewModel.isDisplayed, displayed => !displayed);

        return verticalResizeManager;
    }

    private setupNoEntriesElement() {
        if (!this.viewModel.showNoEntriesLabel)
            return;

        const $gridBody = this.control.find('.GridBodyTable');
        if (!$gridBody.length)
            return;

        const $noEntriesElement = $('<div>').addClass('no-entries');
        this.applyBindings($noEntriesElement, {
            text: ResourceManager.getInstance().get('NO_ENTRIES'),
            visible: this.isNoEntriesElementVisible,
        });
        $noEntriesElement.insertBefore($gridBody);
    }

    protected disposeInternal() {
        // Es kann vorkommen, dass nur das Control und nicht das ViewModel disposed wird.
        // Wenn dabei _waitingForRender true ist, wurde der Busy-Counter bereits erhöht
        // und wir müssen den Counter wieder herunterzählen.
        if (this._waitingForRender && this.viewModel != null && !this.viewModel.isDisposed)
            this.viewModel.decreaseBusyCounter();

        if (this.control != null)
            this.removeEventHandler('click', this.control);

        super.disposeInternal();
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }
}

ComponentManager.setControl('CoreFrameGrid', CoreFrameGridControl, 'RADPresentation.HTMLClient.DefaultViewExtensions');